// Los dos menús que se abren ESCRIBIENDO en una caja de texto: el de colores del `#`
// (ADR-2026-09-21-el-numeral-abre-colores, ADR-2026-09-21-pantone-en-el-menu-de-colores) y
// el de menciones de la `@` (ADR-2026-09-21-la-arroba-menciona-los-adjuntos). Nacieron
// adentro de `BarraEntradaChat`; al pedirlos también la caja de Redes, la lógica se mudó
// acá tal cual y las dos cajas la comparten (ADR-2026-10-04-numeral-y-arroba-en-redes).
//
// El hook guarda el estado (qué token hay bajo el cursor, cuál se escondió con Escape, la
// fila activa, el modo Pantone, los recientes) y devuelve lo que la caja tiene que colgar
// de su textarea —`releerToken` en change/select, `alTeclear` en keydown, `alDesenfocar`
// en blur— más `menus`, el JSX de los dos menús y el picker nativo, que la caja pone al
// lado del textarea dentro de un contenedor `relative` (los menús son `absolute
// bottom-full`, sin portal). El textarea conserva el foco mientras los menús están
// abiertos: ellos apagan su `mousedown`.

import React, { useRef, useState, type RefObject } from 'react';
import type { ChatAttachment } from '../../pages/v2/agentAttachments';
import { MenuDeColores, cantidadDeFilas } from './MenuDeColores';
import {
  conColorInsertado,
  conColorReciente,
  conColoresRecientes,
  guardarColoresRecientes,
  hexesEn,
  leerColoresRecientes,
  normalizarHex,
  opcionesDeColor,
  tokenNumeralEn,
  type ModoDelMenu,
  type OpcionDeColor,
  type TokenNumeral,
} from './coloresDelChat';
import { cargarTablaPantone, pareceCodigoPantone, tablaPantoneCargada } from './pantone';
import { MenuDeMenciones } from './MenuDeMenciones';
import { TextoResaltado } from './TextoResaltado';
import { conMencionInsertada, opcionesDeMencion, tokenDeMencionEn, type OpcionDeMencion, type TokenDeMencion } from './mencionesDelChat';

export interface MenusDeTexto {
  /** En `onChange` y `onSelect` del textarea: vuelve a mirar qué token hay bajo el cursor. */
  releerToken: (el: HTMLTextAreaElement) => void;
  /**
   * En `onKeyDown`, ANTES de lo propio de la caja (Enter que envía, etc.). Devuelve `true`
   * si la tecla era de un menú abierto y ya se manejó (flechas, Enter/Tab, Escape).
   */
  alTeclear: (e: React.KeyboardEvent<HTMLTextAreaElement>) => boolean;
  /** En `onBlur`: sin foco no hay menús, salvo que el foco haya pasado al buscador Pantone. */
  alDesenfocar: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  /** Cierra los dos menús (al pasar a dictado, etc.). */
  cerrar: () => void;
  /** Un color que sale en un texto enviado pasa a "Últimos usados". */
  recordarColoresDe: (texto: string) => void;
  /** Los menús y el picker nativo, para poner al lado del textarea (contenedor `relative`). */
  menus: React.ReactNode;
  /**
   * La capa espejo que resalta `#colores` y `@menciones` detrás del textarea
   * (ADR-2026-10-04-colores-y-menciones-resaltados-en-la-caja). Recibe LAS MISMAS clases
   * de tipografía y padding que el textarea, que a su vez pasa a `text-transparent` con
   * `caret-[#02062B]`; los dos van dentro de un envoltorio `relative`.
   */
  resaltado: (className: string) => React.ReactNode;
  /** En `onScroll` del textarea: la capa espejo scrollea con él. */
  alScrollear: (e: React.UIEvent<HTMLTextAreaElement>) => void;
}

export function useMenusDeTexto(args: {
  valor: string;
  onCambiarValor: (v: string) => void;
  /** Lo que se puede mencionar con `@`. Sin adjuntos la `@` no abre nada. */
  adjuntos: readonly ChatAttachment[];
  textareaRef: RefObject<HTMLTextAreaElement | null>;
}): MenusDeTexto {
  const { valor, onCambiarValor, adjuntos, textareaRef } = args;

  // ── El menú de colores del `#`. El token se recalcula con cada cambio de texto o de
  //    cursor; Escape lo esconde para ESE `#` (vuelve con el próximo). Los recientes viven
  //    en localStorage.
  const [tokenColor, setTokenColor] = useState<TokenNumeral | null>(null);
  const [colorOculto, setColorOculto] = useState<number | null>(null);
  const [colorActivo, setColorActivo] = useState(0);
  // "Buscar color Pantone…" (última fila de la sección Pantone) cambia el menú a la tabla
  // entera con un buscador adentro; un `#` nuevo vuelve al normal.
  const [modoColores, setModoColores] = useState<ModoDelMenu>('colores');
  const [busquedaPantone, setBusquedaPantone] = useState('');
  const menuColoresRef = useRef<HTMLDivElement | null>(null);
  // La tabla Pantone completa se carga la primera vez que hace falta (el buscador, o un
  // código escrito a mano); cuando llega, un re-render la muestra.
  const [, setTablaPantoneVersion] = useState(0);
  const pedirTablaPantone = () => {
    if (tablaPantoneCargada()) return;
    void cargarTablaPantone().then(() => setTablaPantoneVersion((v) => v + 1));
  };
  const [recientes, setRecientes] = useState<string[]>(() =>
    leerColoresRecientes(typeof localStorage === 'undefined' ? null : localStorage),
  );
  const colorRef = useRef<HTMLInputElement | null>(null);
  // El token con el que se abrió el picker: cuando el navegador devuelve el color, el
  // textarea ya perdió el foco y el token de la pantalla puede haberse ido.
  const tokenDelPickerRef = useRef<TokenNumeral | null>(null);
  const menuColores = tokenColor && tokenColor.desde !== colorOculto ? tokenColor : null;
  const opcionesColor = menuColores
    ? opcionesDeColor(modoColores === 'pantone' ? busquedaPantone : menuColores.consulta, recientes, modoColores)
    : [];
  const cambiarModoColores = (m: ModoDelMenu) => {
    setModoColores(m);
    setColorActivo(0);
    // Al entrar a buscar, lo que ya se escribió después del `#` arranca la búsqueda.
    if (m === 'pantone') {
      setBusquedaPantone(menuColores?.consulta ?? '');
      pedirTablaPantone();
    } else window.setTimeout(() => textareaRef.current?.focus(), 0);
  };
  const cerrarMenuColores = () => {
    setTokenColor(null);
    setModoColores('colores');
  };

  // ── El menú de menciones de la `@`: lo que está enganchado en la caja. El token acepta
  //    espacios (los nombres los tienen) y el menú se va solo cuando ya nada coincide;
  //    Escape lo esconde para ESA `@`.
  const [tokenMencion, setTokenMencion] = useState<TokenDeMencion | null>(null);
  const [mencionOculta, setMencionOculta] = useState<number | null>(null);
  const [mencionActiva, setMencionActiva] = useState(0);
  const opcionesMencion = tokenMencion && tokenMencion.desde !== mencionOculta ? opcionesDeMencion(adjuntos, tokenMencion.consulta) : [];
  const menuMenciones = tokenMencion && tokenMencion.desde !== mencionOculta && opcionesMencion.length > 0 ? tokenMencion : null;

  const enfocarEn = (cursor: number) => {
    window.setTimeout(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(cursor, cursor);
    }, 0);
  };
  const insertarMencion = (o: OpcionDeMencion, token: TokenDeMencion) => {
    const { texto, cursor } = conMencionInsertada(valor, token, o.nombre);
    onCambiarValor(texto);
    setTokenMencion(null);
    // Y se esconde para ESA `@` (como Escape): al devolver el foco, `setSelectionRange`
    // dispara `onSelect`, se relee el token y —como el token admite espacios— el
    // `@Nombre ` recién insertado vuelve a coincidir con el mismo adjunto y el menú se
    // reabría; había que elegir dos veces. Una `@` nueva lo abre normal.
    setMencionOculta(token.desde);
    enfocarEn(cursor);
  };

  const releerToken = (el: HTMLTextAreaElement) => {
    const cursor = el.selectionStart ?? el.value.length;
    const t = tokenNumeralEn(el.value, cursor);
    setTokenColor((prev) => (prev?.desde === t?.desde && prev?.consulta === t?.consulta ? prev : t));
    if (t?.desde !== tokenColor?.desde) {
      setColorActivo(0);
      setModoColores('colores');
    }
    if (t && pareceCodigoPantone(t.consulta)) pedirTablaPantone();
    const m = adjuntos.length > 0 ? tokenDeMencionEn(el.value, cursor) : null;
    setTokenMencion((prev) => (prev?.desde === m?.desde && prev?.consulta === m?.consulta ? prev : m));
    if (m?.desde !== tokenMencion?.desde) setMencionActiva(0);
  };
  const recordarColores = (nuevos: string[]) => {
    setRecientes(nuevos);
    guardarColoresRecientes(typeof localStorage === 'undefined' ? null : localStorage, nuevos);
  };
  const insertarColor = (hex: string, token: TokenNumeral, sufijo = '') => {
    const { texto, cursor } = conColorInsertado(valor, token, hex, sufijo);
    onCambiarValor(texto);
    setTokenColor(null);
    setModoColores('colores');
    recordarColores(conColorReciente(recientes, hex));
    enfocarEn(cursor);
  };
  const abrirPicker = () => {
    if (!menuColores) return;
    tokenDelPickerRef.current = menuColores;
    colorRef.current?.click();
  };

  const alTeclear = (e: React.KeyboardEvent<HTMLTextAreaElement>): boolean => {
    // Con el menú de colores abierto, las flechas lo recorren, Enter/Tab eligen y Escape
    // lo esconde; el resto sigue escribiendo (y filtra).
    if (menuColores) {
      // Las filas: las opciones y, al final de la sección Pantone, "Buscar color Pantone…".
      const n = cantidadDeFilas(opcionesColor, modoColores);
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        if (n > 0) setColorActivo((i) => (e.key === 'ArrowDown' ? (i + 1) % n : (i - 1 + n) % n));
        return true;
      }
      if ((e.key === 'Enter' || e.key === 'Tab') && !e.shiftKey && n > 0) {
        e.preventDefault();
        const i = Math.min(colorActivo, n - 1);
        if (i >= opcionesColor.length) {
          cambiarModoColores('pantone');
          return true;
        }
        const elegida: OpcionDeColor = opcionesColor[i];
        insertarColor(elegida.hex, menuColores, elegida.sufijo);
        return true;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        setColorOculto(menuColores.desde);
        return true;
      }
    }
    if (menuMenciones) {
      const n = opcionesMencion.length;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setMencionActiva((i) => (e.key === 'ArrowDown' ? (i + 1) % n : (i - 1 + n) % n));
        return true;
      }
      if ((e.key === 'Enter' || e.key === 'Tab') && !e.shiftKey) {
        e.preventDefault();
        insertarMencion(opcionesMencion[Math.min(mencionActiva, n - 1)], menuMenciones);
        return true;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        setMencionOculta(menuMenciones.desde);
        return true;
      }
    }
    return false;
  };

  const alDesenfocar = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    // Sin foco no hay menú de colores (un click en un ítem no desenfoca: el menú apaga su
    // `mousedown`), salvo que el foco haya pasado al buscador Pantone, que vive adentro
    // del menú. Volver a la caja lo reabre por `onSelect`.
    const destino = e.relatedTarget as Node | null;
    if (destino && menuColoresRef.current?.contains(destino)) return;
    cerrarMenuColores();
    setTokenMencion(null);
  };

  const cerrar = () => {
    cerrarMenuColores();
    setTokenMencion(null);
  };

  const espejoRef = useRef<HTMLDivElement | null>(null);
  const resaltado = (className: string) => (
    <TextoResaltado ref={espejoRef} texto={valor} adjuntos={adjuntos} className={className} />
  );
  const alScrollear = (e: React.UIEvent<HTMLTextAreaElement>) => {
    const espejo = espejoRef.current;
    if (!espejo) return;
    espejo.scrollTop = e.currentTarget.scrollTop;
    espejo.scrollLeft = e.currentTarget.scrollLeft;
  };

  const recordarColoresDe = (texto: string) => {
    const hexes = hexesEn(texto);
    if (hexes.length > 0) recordarColores(conColoresRecientes(recientes, hexes));
  };

  const menus = (
    <>
      {menuMenciones && (
        <MenuDeMenciones
          opciones={opcionesMencion}
          activo={Math.min(mencionActiva, opcionesMencion.length - 1)}
          onActivar={setMencionActiva}
          onElegir={(o) => insertarMencion(o, menuMenciones)}
        />
      )}
      {menuColores && (
        <MenuDeColores
          opciones={opcionesColor}
          activo={Math.min(colorActivo, Math.max(0, opcionesColor.length - 1))}
          onActivar={setColorActivo}
          onElegir={(o) => insertarColor(o.hex, menuColores, o.sufijo)}
          onNuevo={abrirPicker}
          modo={modoColores}
          onCambiarModo={cambiarModoColores}
          busqueda={busquedaPantone}
          onBusqueda={(t) => {
            setBusquedaPantone(t);
            setColorActivo(0);
          }}
          onSalir={cerrarMenuColores}
          raizRef={menuColoresRef}
        />
      )}
      {/* El picker nativo de "Nuevo color": siempre montado (el diálogo del navegador
          contesta aunque el menú ya se haya ido) e invisible sin `display:none`, que en
          algunos navegadores no deja abrirlo por `click()`. */}
      <input
        ref={colorRef}
        type="color"
        tabIndex={-1}
        aria-hidden="true"
        className="pointer-events-none absolute bottom-full left-3 h-0 w-0 opacity-0"
        onChange={(e) => {
          const hex = normalizarHex(e.target.value);
          const token = tokenDelPickerRef.current;
          tokenDelPickerRef.current = null;
          if (hex && token) insertarColor(hex, token);
        }}
      />
    </>
  );

  return { releerToken, alTeclear, alDesenfocar, cerrar, recordarColoresDe, menus, resaltado, alScrollear };
}
